/**
 * Change history — every change to settings, disclosures and configuration,
 * searchable, with before → after and a Restore. Bill 2026-10-08: "make sure
 * all changes to settings and/or disclosures and configurations are logged
 * and researchable/auditable in case we need to triage and restore
 * improperly set or incomplete changes to the system."
 *
 * Source: dbo.ConfigChangeLog, written automatically for every configuration
 * save (any screen, any panel) and every raw SQL seed run. Restore undoes one
 * change; if the row changed again since, it shows what would be overwritten
 * and asks again. The restore is itself recorded here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, type ConfigChangeRow, type ConfigRestoreResult } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner, Empty } from '@/components/primitives';
import './changeHistory.css';

type Values = Record<string, unknown>;

function parse(json: string | null): Values {
  if (!json) return {};
  try { return JSON.parse(json) as Values; } catch { return { value: json }; }
}

function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v === '' ? '(empty)' : v;
  return JSON.stringify(v);
}

function when(iso: string): string {
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString();
}

function keyText(row: ConfigChangeRow): string {
  const k = parse(row.keyJson);
  return Object.values(k).map(show).join(' · ') || '—';
}

const OP_TONE: Record<string, 'ok' | 'warn' | 'err' | 'info' | 'muted'> = { Insert: 'ok', Update: 'info', Delete: 'err', SeedSql: 'warn' };
const OP_LABEL: Record<string, string> = { Insert: 'Added', Update: 'Changed', Delete: 'Deleted', SeedSql: 'SQL script run' };

export function ChangeHistoryPage(): JSX.Element {
  const client = useApiClient();
  const [params, setParams] = useSearchParams();
  const [tables, setTables] = useState<ReadonlyArray<{ tableName: string; changes: number }>>([]);
  const [rows, setRows] = useState<ConfigChangeRow[]>([]);
  const [more, setMore] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(() => ({
    table: params.get('table') ?? '', op: params.get('op') ?? '', actor: params.get('actor') ?? '',
    q: params.get('q') ?? '', from: params.get('from') ?? '', to: params.get('to') ?? '',
  }));

  const query = useMemo(() => ({
    table: params.get('table') ?? '', op: params.get('op') ?? '', actor: params.get('actor') ?? '',
    q: params.get('q') ?? '', from: params.get('from') ?? '', to: params.get('to') ?? '',
  }), [params]);

  const load = useCallback(async (before?: number) => {
    setLoading(true); setError(null);
    try {
      const r = await client.retail.configChanges.list({ ...query, before, take: 50 });
      setRows((prev) => (before ? [...prev, ...r.data] : [...r.data]));
      setMore(r.more ? r.nextBefore : null);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not load the change history.');
    } finally {
      setLoading(false);
    }
  }, [client, query]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    void client.retail.configChanges.tables().then((r) => setTables(r.data)).catch(() => undefined);
  }, [client]);

  function search(): void {
    const next = new URLSearchParams();
    for (const [k, v] of Object.entries(draft)) if (v.trim()) next.set(k, v.trim());
    setParams(next);
  }
  function clear(): void {
    setDraft({ table: '', op: '', actor: '', q: '', from: '', to: '' });
    setParams(new URLSearchParams());
  }

  return (
    <div className="ch-page">
      <header className="ch-head">
        <h1>Change history</h1>
        <p className="page-intro">
          Every change to settings, disclosures and configuration - from any screen or panel - and every SQL
          script run against the database: who made it, when, what it was before and after, and why. Use
          <strong> Restore</strong> to undo a change; the restore is recorded here too.
        </p>
      </header>

      <Card title="Find changes">
        <div className="ch-filters">
          <label>Table
            <select value={draft.table} onChange={(e) => setDraft({ ...draft, table: e.target.value })}>
              <option value="">Any table</option>
              {tables.map((t) => <option key={t.tableName} value={t.tableName}>{t.tableName} ({t.changes})</option>)}
            </select>
          </label>
          <label>What happened
            <select value={draft.op} onChange={(e) => setDraft({ ...draft, op: e.target.value })}>
              <option value="">Anything</option>
              <option value="Update">Changed</option>
              <option value="Insert">Added</option>
              <option value="Delete">Deleted</option>
              <option value="SeedSql">SQL script run</option>
            </select>
          </label>
          <label>Who
            <input value={draft.actor} placeholder="e.g. jessica" onChange={(e) => setDraft({ ...draft, actor: e.target.value })} />
          </label>
          <label>Contains
            <input value={draft.q} placeholder="setting key, template code, value, reason…" onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />
          </label>
          <label>From
            <input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
          </label>
          <label>To (before)
            <input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
          </label>
        </div>
        <div className="ch-filters__actions">
          <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
          <Button variant="primary" size="sm" onClick={search}>Search</Button>
        </div>
      </Card>

      {error && <Banner tone="err">{error}</Banner>}
      {!loading && rows.length === 0 && !error && <Empty>No changes match. Recording started 2026-10-08.</Empty>}

      <ul className="ch-list">
        {rows.map((r) => <ChangeItem key={r.changeId} row={r} onRestored={() => void load()} />)}
      </ul>

      {loading && <Spinner label="Loading changes…" />}
      {more !== null && !loading && (
        <div className="ch-more"><Button variant="ghost" onClick={() => void load(more)}>Show older changes</Button></div>
      )}
    </div>
  );
}

function ChangeItem({ row, onRestored }: { row: ConfigChangeRow; onRestored: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const before = parse(row.oldValuesJson);
  const after = parse(row.newValuesJson);
  const columns = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  const seed = row.operation === 'SeedSql';

  return (
    <li className="ch-item">
      <div className="ch-item__top">
        <Pill tone={OP_TONE[row.operation] ?? 'muted'}>{OP_LABEL[row.operation] ?? row.operation}</Pill>
        <strong className="ch-item__table">{row.tableName}</strong>
        <code className="cell-mono">{keyText(row)}</code>
        <span className="ch-item__spacer" />
        <span className="cell-dim">#{row.changeId} · {when(row.changedAt)}</span>
      </div>
      <div className="ch-item__meta">
        <span>by <code className="cell-mono">{row.actor}</code></span>
        {row.source && <span className="cell-dim">via {row.source}{row.app ? ` (${row.app})` : ''}</span>}
        {row.restoresChangeId && <Pill tone="warn">Restore of #{row.restoresChangeId}</Pill>}
        {row.restoredBy.map((b) => <Pill key={b.changeId} tone="muted">Undone by {b.actor} (#{b.changeId})</Pill>)}
      </div>
      {row.reason && <p className="ch-item__reason"><span className="cell-dim">Why:</span> {row.reason}</p>}

      <div className="ch-item__actions">
        <button type="button" className="link" onClick={() => setOpen((v) => !v)}>{open ? '▾ Hide details' : `▸ ${seed ? 'Show the script' : 'Show what changed'}`}</button>
      </div>

      {open && !seed && (
        <table className="data-table ch-diff">
          <thead><tr><th>Field</th>{row.operation !== 'Insert' && <th>Before</th>}{row.operation !== 'Delete' && <th>After</th>}</tr></thead>
          <tbody>
            {columns.map((c) => (
              <tr key={c}>
                <td><code className="cell-mono">{c}</code></td>
                {row.operation !== 'Insert' && <td className="ch-diff__old">{show(before[c])}</td>}
                {row.operation !== 'Delete' && <td className="ch-diff__new">{show(after[c])}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && seed && (
        <div className="ch-seed">
          <p className="cell-dim">
            {show(after.name)} · outcome {show(after.outcome)} · {show(after.rowsAffected)} row(s) · SHA-256 <code className="cell-mono">{show(after.sha256)}</code>.
            A script cannot be undone automatically - reverse it by hand.
          </p>
          <pre className="ch-seed__sql">{show(after.sql)}</pre>
        </div>
      )}

      {row.canRestore && <RestorePanel row={row} onRestored={onRestored} />}
    </li>
  );
}

function RestorePanel({ row, onRestored }: { row: ConfigChangeRow; onRestored: () => void }): JSX.Element {
  const client = useApiClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConfigRestoreResult | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const what = row.operation === 'Update' ? 'put the earlier values back'
    : row.operation === 'Insert' ? 'remove the row this change added'
    : 'put the deleted row back';

  async function go(force: boolean): Promise<void> {
    setBusy(true); setErrorText(null);
    try {
      const r = await client.retail.configChanges.restore(row.changeId, { reason: reason.trim(), force });
      setResult(r);
      if (r.success) window.setTimeout(onRestored, 1200);
    } catch (e) {
      const body = e instanceof ApiError ? (e.details as ConfigRestoreResult | undefined) : undefined;
      if (body && typeof body === 'object' && 'code' in body) setResult(body);
      else setErrorText((e as Error)?.message ?? 'Could not restore.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return <div className="ch-item__actions"><Button variant="ghost" size="sm" onClick={() => setOpen(true)}>Restore…</Button></div>;
  }
  return (
    <div className="ch-restore">
      <p>Restoring change #{row.changeId} will <strong>{what}</strong> on {row.tableName}. It is recorded in this history under your name.</p>
      <label className="ch-restore__reason">Why are you restoring it? <em>required</em>
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Cut-off was set to the wrong value during testing" />
      </label>
      {errorText && <Banner tone="err">{errorText}</Banner>}
      {result && result.success && <Banner tone="info">{result.message}</Banner>}
      {result && !result.success && (
        <Banner tone={result.code === 'CHANGED_SINCE' ? 'warn' : 'err'}>
          {result.message}
          {result.conflicts && result.conflicts.length > 0 && (
            <ul className="ch-restore__conflicts">
              {result.conflicts.map((c) => (
                <li key={c.column}><code className="cell-mono">{c.column}</code>: was {c.expectedJson ?? '—'} after this change, is now {c.currentJson ?? '—'}</li>
              ))}
            </ul>
          )}
        </Banner>
      )}
      <div className="ch-restore__actions">
        <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setResult(null); }} disabled={busy}>Cancel</Button>
        {result?.code === 'CHANGED_SINCE' ? (
          <Button variant="danger" size="sm" disabled={busy || reason.trim().length < 5} onClick={() => void go(true)}>
            {busy ? 'Restoring…' : 'Restore anyway (overwrite later changes)'}
          </Button>
        ) : !result?.success && (
          <Button variant="primary" size="sm" disabled={busy || reason.trim().length < 5} onClick={() => void go(false)}>
            {busy ? 'Restoring…' : 'Restore this change'}
          </Button>
        )}
      </div>
    </div>
  );
}

export default ChangeHistoryPage;
